import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Pencil, Download,
  Loader2, AlertTriangle, Instagram
} from 'lucide-react';

const CarouselViewer = ({ batch, contents, onPublish }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Ordenar contents pelo carousel_index
  const sortedContents = [...contents].sort((a, b) => {
    const indexA = a.metadata?.carousel_index ?? 0;
    const indexB = b.metadata?.carousel_index ?? 0;
    return indexA - indexB;
  });

  const currentContent = sortedContents[currentIndex];
  const total = sortedContents.length;

  const goNext = () => {
    if (currentIndex < total - 1) setCurrentIndex(currentIndex + 1);
  };

  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleEdit = (content) => {
    if (content.model === 'creatomate' && content.metadata?.render_script) {
      navigate(`/contents/batch/${batch.id}/edit-creatomate/${content.id}`);
    } else if (content.model === 'templated') {
      navigate(`/contents/batch/${batch.id}/edit-templated/${content.id}`);
    } else if (content.model === 'react_render') {
      navigate(`/contents/batch/${batch.id}/edit-react-render/${content.id}`);
    } else if (content.model === 'gpt_image') {
      // ✅ Fase 4: editor conversacional em tela dedicada
      navigate(`/contents/batch/${batch.id}/edit-gpt-image/${content.id}`);
    }
  };

  const handleDownload = async (content) => {
    if (!content.result_url) return;
    try {
      const response = await fetch(content.result_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `carousel-slide-${(content.metadata?.carousel_index ?? currentIndex) + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      window.open(content.result_url, '_blank');
    }
  };

  const allCompleted = sortedContents.every(c => c.status === 'completed');
  const anyProcessing = sortedContents.some(c => c.status === 'processing' || c.status === 'pending');

  return (
    <div className="flex flex-col items-center gap-6">
      {/* Carousel Container */}
      <div className="relative w-full max-w-lg">
        {/* Main Image */}
        <div className="relative aspect-square bg-gray-100 rounded-2xl overflow-hidden shadow-lg">
          {/* ✅ Fase 4: indicador de edição IA em andamento neste slide */}
          {currentContent?.metadata?.edit_job?.status === 'processing' && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 z-20">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
              <span className="text-white text-sm font-semibold">Editando com IA…</span>
            </div>
          )}
          {currentContent?.status === 'completed' && currentContent?.result_url ? (
            <img
              src={currentContent.result_url}
              alt={`Slide ${currentIndex + 1}`}
              className="w-full h-full object-cover"
            />
          ) : currentContent?.status === 'failed' ? (
            <div className="w-full h-full flex flex-col items-center justify-center text-red-500 gap-3">
              <AlertTriangle className="w-12 h-12" />
              <span className="text-sm font-medium">Falha ao gerar slide</span>
              <span className="text-xs text-gray-500">{currentContent?.error_message || 'Erro desconhecido'}</span>
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-10 h-10 text-purple-500 animate-spin" />
              <span className="text-sm text-gray-500 font-medium">
                {currentContent?.status === 'processing' ? 'Gerando slide...' : 'Aguardando...'}
              </span>
            </div>
          )}

          {/* Slide counter badge */}
          <div className="absolute top-3 right-3 px-3 py-1 bg-black/60 rounded-full text-white text-sm font-medium backdrop-blur-sm">
            {currentIndex + 1} / {total}
          </div>

          {/* Edit badge */}
          {currentContent?.status === 'completed' && currentContent?.metadata?.render_script && (
            <div className="absolute top-3 left-3 px-2 py-1 bg-purple-500/90 rounded-lg text-white text-xs font-medium backdrop-blur-sm">
              Editável
            </div>
          )}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goPrev}
          disabled={currentIndex === 0}
          className={`
            absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2
            w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center
            transition-all
            ${currentIndex === 0
              ? 'opacity-30 cursor-not-allowed'
              : 'hover:bg-purple-50 hover:shadow-xl hover:scale-110'
            }
          `}
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>

        <button
          onClick={goNext}
          disabled={currentIndex === total - 1}
          className={`
            absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2
            w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center
            transition-all
            ${currentIndex === total - 1
              ? 'opacity-30 cursor-not-allowed'
              : 'hover:bg-purple-50 hover:shadow-xl hover:scale-110'
            }
          `}
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* Dots indicator */}
      <div className="flex gap-2">
        {sortedContents.map((content, i) => (
          <button
            key={content.id}
            onClick={() => setCurrentIndex(i)}
            className={`
              w-3 h-3 rounded-full transition-all
              ${i === currentIndex
                ? 'bg-purple-500 scale-125'
                : content.status === 'completed'
                  ? 'bg-purple-200 hover:bg-purple-300'
                  : content.status === 'failed'
                    ? 'bg-red-300'
                    : 'bg-gray-300 animate-pulse'
              }
            `}
          />
        ))}
      </div>

      {/* Action buttons for current slide */}
      {currentContent?.status === 'completed' && (
        <div className="flex gap-3">
          {currentContent.metadata?.render_script && (
            <button
              onClick={() => handleEdit(currentContent)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-xl hover:bg-purple-200 transition-colors font-medium text-sm"
            >
              <Pencil className="w-4 h-4" />
              Editar Slide {currentIndex + 1}
            </button>
          )}
          <button
            onClick={() => handleDownload(currentContent)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium text-sm"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        </div>
      )}

      {/* Publish carousel button */}
      {allCompleted && (
        <button
          onClick={() => onPublish && onPublish(batch)}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all shadow-md font-semibold"
        >
          <Instagram className="w-5 h-5" />
          Publicar Carrossel no Instagram
        </button>
      )}

      {/* Thumbnail strip */}
      <div className="flex gap-2 mt-2">
        {sortedContents.map((content, i) => (
          <button
            key={content.id}
            onClick={() => setCurrentIndex(i)}
            className={`
              w-16 h-16 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0
              ${i === currentIndex
                ? 'border-purple-500 shadow-md scale-105'
                : 'border-transparent opacity-60 hover:opacity-100'
              }
            `}
          >
            {content.status === 'completed' && content.result_url ? (
              <img src={content.result_url} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                {content.status === 'failed' ? (
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                ) : (
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                )}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CarouselViewer;
